import {Injectable} from '@angular/core';
import {DcpDataService} from '../dcp-data.service';
import {EddlAdobeTrackingData, EddlUserAttributes} from './models/eddl-adobe-tracking-data';
import {CxEvent} from '@spartacus/core';
import {DcpEddlLoginStateService} from './dcp-eddl-login-state.service';
import {EddlLoginEvent} from './events/eddl-login-event';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, Observable, switchMap} from 'rxjs';
import {EddlAfterInitializationEvent} from '@app/services/analytics/EDDL/events/eddl-after-initialization-event';
import {distinctUntilChanged, filter, first, map} from 'rxjs/operators';
import {DcpAuthService} from '@app/auth/services/dcp-auth.service';
import {getIDSid} from './utils/sbn-user-check';
import {USER_ATTRIBUTES_JWT_FIELDS} from './utils/user-attributes';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpEddlLoginDataService extends DcpDataService<EddlAdobeTrackingData> {
  private hasBeenInitializedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isLoggedIn: boolean = false;

  constructor(protected authService: DcpAuthService, protected loginStateService: DcpEddlLoginStateService) {
    super();
  }

  fillData(event: EddlLoginEvent, data?: EddlAdobeTrackingData): Observable<EddlAdobeTrackingData> {
    if (!data) {
      data = {};
    }
    if (!data.user) {
      data.user = {};
    }

    data.event = 'userLogin';
    data.user.loginStatus = 'yes';
    data.user.loginMethod = this.loginStateService.getLoginMethod();
    return this.loginStateService.getUser().pipe(
      distinctUntilChanged(),
      filter((x) => x !== undefined),
      map((user) => {
        const detailsFromToken = this.authService.getParsedJwtToken<EddlUserAttributes>(USER_ATTRIBUTES_JWT_FIELDS);

        data.user.type = user?.email?.includes('@sap.com') ? 'employee' : 'customer';
        data.user.scimUserId = user?.scimUserId ?? '';
        data.user.account = {IDSid: getIDSid(user?.scimUserId ?? '')};

        return {
          ...data,
          user: {
            ...data.user,
            ...(detailsFromToken ?? {}),
          },
        };
      }),
      untilDestroyed(this)
    );
  }

  getData(event: CxEvent): Observable<EddlAdobeTrackingData> | EddlAdobeTrackingData {
    if (event instanceof EddlAfterInitializationEvent) {
      this.hasBeenInitializedSubject.next(true);
    }
    if (!(event instanceof EddlLoginEvent)) {
      return undefined;
    }

    return this.hasBeenInitializedSubject.pipe(
      untilDestroyed(this),
      distinctUntilChanged(),
      filter(Boolean),
      first(),
      switchMap(() => this.fillData(event))
    );
  }
}
